<!-- NOTICE: KAPTION-INTEGRATION -->
<script>
import { mapGetters } from 'vuex';
// NOTICE: KAPTION-INTEGRATION - Import shared SDK utilities
import {
  DashboardAppSDKUtils,
  SDK_MESSAGE_TYPES,
  SDK_EVENT_TYPES,
} from 'dashboard/composables/useDashboardAppSDK';
// NOTICE: KAPTION-INTEGRATION - openConversation deep-link helper
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import ConversationApi from 'dashboard/api/inbox/conversation';

export default {
  props: {
    config: {
      type: Array,
      default: () => [],
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
    isVisible: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Number,
      required: true,
    },
    dashboardAppId: {
      type: Number,
      default: null,
    },
    // NOTICE: KAPTION-INTEGRATION
    surface: {
      type: String,
      default: 'main',
      validator: value => ['main', 'sidebar', 'full'].includes(value),
    },
    sidebarParams: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['frameLoaded', 'frameClosedAfterNavigation'],
  data() {
    return {
      // NOTICE: KAPTION-INTEGRATION - Always render iframe immediately (no lazy loading)
      // This ensures the iframe is created once and never destroyed, just shown/hidden via v-show
      hasOpenedAtleastOnce: true,
      // NOTICE: KAPTION-INTEGRATION - No loading state - iframe is always ready
      iframeLoading: false,
      tokens: {}, // NOTICE: KAPTION-INTEGRATION - Store tokens per iframe
      initialSrcs: {}, // NOTICE: KAPTION-INTEGRATION - Cache first iframe src to prevent reloads

      // Inter-App Tool & Context Protocol — broker state
      // Registry: { [appId]: { tools: ToolDef[], contexts: ContextDef[], iframeId: string } }
      appRegistry: {},
      // Pending invocations: { [containerRequestId]: { callerRequestId, callerWindow, targetApp, tool, timeoutId } }
      brokerPendingInvocations: {},
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),
    dashboardAppContext() {
      return {
        conversation: this.currentChat,
        contact: this.$store.getters['contacts/getContact'](this.contactId),
        currentAgent: this.currentAgent,
      };
    },
    contactId() {
      return this.currentChat?.meta?.sender?.id;
    },
    // NOTICE: KAPTION-INTEGRATION - Stable fingerprint of meaningful context data
    // Only changes when conversation, contact, or agent actually change identity
    contextFingerprint() {
      const conv = this.currentChat;
      const contact = this.$store.getters['contacts/getContact'](
        this.contactId
      );
      return [
        conv?.id,
        conv?.status,
        conv?.meta?.sender?.id,
        contact?.id,
        contact?.name,
        contact?.email,
        this.currentUser?.id,
      ].join(':');
    },
    currentAgent() {
      const { id, name, email } = this.currentUser;
      return { id, name, email };
    },
    // NOTICE: KAPTION-INTEGRATION - Check if JWT mode is enabled via feature flag
    isJwtModeEnabled() {
      if (!this.dashboardAppId) return false;
      return this.$store.getters['kaptionFeatureFlags/isEnabled'](
        'KAPTION_DASHBOARD_APPS_JWT',
        this.accountId,
        this.currentUser.id
      );
    },
  },
  watch: {
    // NOTICE: KAPTION-INTEGRATION - Send context when frame becomes visible
    isVisible: {
      handler(newVal) {
        if (newVal) {
          // Frame just became visible, send context and preferences
          this.$nextTick(() => {
            this.sendContextToIframes(this.dashboardAppContext);
            this.config.forEach((configItem, index) => {
              const frameElement = document.getElementById(
                this.getFrameId(index)
              );
              if (frameElement) {
                this.sendUserPreferences(frameElement);
              }
            });
          });
        }
      },
    },
    // NOTICE: KAPTION-INTEGRATION - Watch for conversation changes and send context
    // Only send when meaningful data changes (conversation id, contact id, agent id)
    // to avoid re-rendering dashboard apps on every Vuex store mutation
    contextFingerprint: {
      handler() {
        if (this.isVisible) {
          this.sendContextToIframes(this.dashboardAppContext);
        }
      },
    },
  },
  async mounted() {
    window.addEventListener('message', this.triggerEvent);
    // NOTICE: KAPTION-INTEGRATION - Pre-fetch token so getIframeSrc() includes it on first render
    if (this.isJwtModeEnabled && this.dashboardAppId) {
      const token = await this.fetchTokenForApp();
      if (token) {
        this.config.forEach((_, index) => {
          this.tokens = { ...this.tokens, [index]: token };
        });
      }
    }
  },
  unmounted() {
    window.removeEventListener('message', this.triggerEvent);
    this.cleanupBrokerState();
  },
  methods: {
    // NOTICE: KAPTION-INTEGRATION - Check if event came from one of our iframes
    isEventFromOurIframe(event) {
      if (!event.source) return false;
      // Check each iframe managed by this Frame.vue instance
      for (let i = 0; i < this.config.length; i += 1) {
        const frameElement = document.getElementById(this.getFrameId(i));
        if (frameElement && frameElement.contentWindow === event.source) {
          return true;
        }
      }
      return false;
    },
    triggerEvent(event) {
      // NOTICE: KAPTION-INTEGRATION - Handle SDK events that work even when not visible
      // Badge/notification events should always be processed so badges can appear on inactive tabs
      if (DashboardAppSDKUtils.isSDKEvent(event.data)) {
        // NOTICE: KAPTION-INTEGRATION - Only process if event came from OUR iframe
        // This prevents duplicate handling when multiple Frame.vue instances exist
        if (!this.isEventFromOurIframe(event)) {
          return;
        }

        const { sdkType, payload } = event.data.data || {};

        // These events work regardless of visibility
        switch (sdkType) {
          // NOTICE: KAPTION-INTEGRATION - Badge events work even when tab is not active
          case SDK_EVENT_TYPES.SET_BADGE:
          case 'set-badge':
            this.handleSetBadge(payload, event.source);
            return;
          case SDK_EVENT_TYPES.CLEAR_BADGE:
          case 'clear-badge':
            this.handleClearBadge(payload, event.source);
            return;
          // NOTICE: KAPTION-INTEGRATION - New notification API
          case 'send-notification':
            this.handleSendNotification(payload, event.source);
            return;
          case 'dismiss-notification':
            this.handleDismissNotification(payload, event.source);
            return;
          // NOTICE: KAPTION-INTEGRATION - Log events work regardless of visibility
          case SDK_EVENT_TYPES.LOG:
          case 'log':
            this.handleLog(payload);
            return;
          // NOTICE: KAPTION-INTEGRATION - Message/composer events work even when sidebar closed
          // This allows notification action buttons to send messages from the dropdown
          case SDK_EVENT_TYPES.SEND_MESSAGE:
          case 'send-message':
            this.handleSendMessage(payload, event.source);
            return;
          case SDK_EVENT_TYPES.COPY_TO_COMPOSER:
          case 'copy-to-composer':
            this.handleCopyToComposer(payload, event.source);
            return;
          // NOTICE: KAPTION-INTEGRATION - Deep-link the agent to a conversation.
          // Always-process so a full-screen app can navigate even when the
          // dashboard tab isn't currently focused on a conversation.
          case SDK_EVENT_TYPES.OPEN_CONVERSATION:
          case 'open-conversation':
            this.handleOpenConversation(payload, event.source);
            return;

          // Inter-App Tool & Context Protocol events (always process)
          case SDK_EVENT_TYPES.TOOL_REGISTER:
          case 'tool-register':
            this.handleBrokerToolRegister(payload, event.source);
            return;
          case SDK_EVENT_TYPES.TOOL_UNREGISTER:
          case 'tool-unregister':
            this.handleBrokerToolUnregister(payload, event.source);
            return;
          case SDK_EVENT_TYPES.CTX_REGISTER:
          case 'ctx-register':
            this.handleBrokerCtxRegister(payload, event.source);
            return;
          case SDK_EVENT_TYPES.CTX_UNREGISTER:
          case 'ctx-unregister':
            this.handleBrokerCtxUnregister(payload, event.source);
            return;
          case SDK_EVENT_TYPES.TOOL_DISCOVER:
          case 'tool-discover':
            this.handleBrokerToolDiscover(payload, event.source);
            return;
          case SDK_EVENT_TYPES.CTX_DISCOVER:
          case 'ctx-discover':
            this.handleBrokerCtxDiscover(payload, event.source);
            return;
          case SDK_EVENT_TYPES.TOOL_INVOKE:
          case 'tool-invoke':
            this.handleBrokerToolInvoke(payload, event.source);
            return;
          case SDK_EVENT_TYPES.TOOL_RESULT:
          case 'tool-result':
            this.handleBrokerToolResult(payload);
            return;
          case SDK_EVENT_TYPES.TOOL_PARTIAL:
          case 'tool-partial':
            this.handleBrokerToolPartial(payload);
            return;
          case SDK_EVENT_TYPES.TOOL_ERROR:
          case 'tool-error':
            this.handleBrokerToolError(payload);
            return;
          case SDK_EVENT_TYPES.TOOL_CANCEL:
          case 'tool-cancel':
            this.handleBrokerToolCancel(payload);
            return;
          case SDK_EVENT_TYPES.CTX_FETCH:
          case 'ctx-fetch':
            this.handleBrokerCtxFetch(payload, event.source);
            return;
          case SDK_EVENT_TYPES.CTX_RESULT:
          case 'ctx-result':
            this.handleBrokerCtxResult(payload);
            return;
          case SDK_EVENT_TYPES.CTX_ERROR:
          case 'ctx-error':
            this.handleBrokerCtxError(payload);
            return;
          // NOTICE: KAPTION-INTEGRATION - Token refresh request from SDK
          case SDK_EVENT_TYPES.TOKEN_REFRESH:
          case 'token-refresh':
            this.handleTokenRefresh(event.source);
            return;
          default:
            break;
        }
      }

      // Everything below requires the frame to be visible
      if (!this.isVisible) return;

      // NOTICE: KAPTION-INTEGRATION - Only process if event came from OUR iframe
      if (!this.isEventFromOurIframe(event)) {
        return;
      }

      // Handle legacy message using shared utility
      if (DashboardAppSDKUtils.isLegacyFetchInfo(event.data)) {
        this.onIframeLoad(0);
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - Handle SDK ready message using shared utility
      if (DashboardAppSDKUtils.isSDKReadyEvent(event.data)) {
        // eslint-disable-next-line no-console
        console.log('[Frame.vue] SDK ready signal received, resending context');
        this.sendContextToIframes(this.dashboardAppContext);
        // Send preferences to all iframes
        this.config.forEach((configItem, index) => {
          const frameElement = document.getElementById(this.getFrameId(index));
          if (frameElement) {
            this.sendUserPreferences(frameElement);
          }
        });
        // Send initial UI config
        this.sendUIConfigToAllIframes();
        // Push tool/context catalog to newly ready app
        this.pushCatalogToWindow(event.source);
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - Handle SDK events that require visibility
      if (DashboardAppSDKUtils.isSDKEvent(event.data)) {
        const { sdkType } = event.data.data || {};

        switch (sdkType) {
          case SDK_EVENT_TYPES.CONTEXT_REFRESH:
          case 'context-refresh-request':
            this.sendContextToIframes(this.dashboardAppContext);
            break;
          default:
            break;
        }
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Use surface and dashboardAppId for unique frame IDs
    getFrameId(index) {
      const identifier = this.dashboardAppId || this.position;
      return `dashboard-app--frame-${this.surface}-${identifier}-${index}`;
    },
    // NOTICE: KAPTION-INTEGRATION - Fetch token for JWT mode
    async fetchTokenForApp() {
      if (!this.isJwtModeEnabled || !this.dashboardAppId) return null;

      try {
        // Check if token already exists
        const existingToken = this.$store.getters['dashboardApps/getToken'](
          this.dashboardAppId
        );
        if (existingToken) {
          return existingToken;
        }

        // Fetch token from API
        const response = await this.$store.dispatch(
          'dashboardApps/fetchToken',
          this.dashboardAppId
        );
        return response.token;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch dashboard app token:', error);
        return null;
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle token refresh request from SDK
    async handleTokenRefresh(sourceWindow) {
      if (!this.isJwtModeEnabled || !this.dashboardAppId) return;

      try {
        const response = await this.$store.dispatch(
          'dashboardApps/fetchToken',
          this.dashboardAppId
        );
        const newToken = response.token;
        const expiresIn = response.expires_in;

        // NOTE: Do NOT update this.tokens here — that would change iframe src
        // and cause a full reload. The token is delivered via postMessage below.

        // Send refreshed token via postMessage (no iframe reload)
        const message = DashboardAppSDKUtils.buildTokenRefreshedMessage(
          newToken,
          expiresIn
        );
        if (sourceWindow) {
          try {
            sourceWindow.postMessage(JSON.stringify(message), '*');
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('[Frame.vue] Failed to send refreshed token:', error);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Token refresh failed:', error);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Send user preferences (theme and language)
    sendUserPreferences(frameElement) {
      if (!frameElement || !frameElement.contentWindow) return;

      const uiSettings = this.$store.getters.getUISettings;
      const theme = uiSettings?.theme || 'light';
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Use shared utility to build message
      const message = DashboardAppSDKUtils.buildSettingsMessage(
        theme,
        this.$i18n.locale,
        timezone,
        this.surface,
        this.sidebarParams
      );

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] Sending user preferences:', message);
      DashboardAppSDKUtils.postMessageToIframe(
        frameElement,
        message,
        '[Frame.vue]'
      );
    },
    // NOTICE: KAPTION-INTEGRATION - Send context to iframes
    sendContextToIframes(context) {
      // eslint-disable-next-line no-console
      console.log('[Frame.vue] Sending context to iframes:', {
        conversationId: context.conversation?.id,
        contactId: context.contact?.id,
        agent: context.currentAgent?.name,
        jwtMode: this.isJwtModeEnabled,
        surface: this.surface,
      });
      this.config.forEach((configItem, index) => {
        const frameElement = document.getElementById(this.getFrameId(index));
        if (!frameElement || !frameElement.contentWindow) return;

        if (this.isJwtModeEnabled) {
          // JWT mode: Use shared utility to build message
          const message = DashboardAppSDKUtils.buildContextMessage(
            context,
            this.surface,
            this.sidebarParams
          );
          DashboardAppSDKUtils.postMessageToIframe(
            frameElement,
            message,
            '[Frame.vue]'
          );
        } else {
          // Legacy mode: Send appContext event
          const eventData = {
            event: SDK_MESSAGE_TYPES.LEGACY_APP_CONTEXT,
            data: {
              ...context,
              ui: {
                surface: this.surface,
                sidebarParams: this.sidebarParams,
              },
            },
          };
          frameElement.contentWindow.postMessage(
            JSON.stringify(eventData),
            '*'
          );
        }
      });
    },
    async onIframeLoad(index) {
      const frameElement = document.getElementById(this.getFrameId(index));
      if (!frameElement) return;

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] Iframe loaded', {
        index,
        jwtMode: this.isJwtModeEnabled,
        appId: this.dashboardAppId,
      });

      // NOTICE: KAPTION-INTEGRATION - Handle JWT mode
      if (this.isJwtModeEnabled) {
        // Fetch token if not already cached
        const token = await this.fetchTokenForApp();
        if (token) {
          this.tokens[index] = token;
          // eslint-disable-next-line no-console
          console.log(
            '[Frame.vue] Token fetched for dashboard app, waiting for SDK ready signal'
          );
        }
        // DO NOT send context/preferences here - wait for SDK ready signal
      } else {
        // Legacy mode: Send JSON string immediately (no SDK ready handshake)
        const eventData = {
          event: 'appContext',
          data: this.dashboardAppContext,
        };
        frameElement.contentWindow.postMessage(JSON.stringify(eventData), '*');
      }

      this.iframeLoading = false;
      // NOTICE: KAPTION-INTEGRATION - Emit frame-loaded event for preloader
      this.$emit('frameLoaded', this.dashboardAppId);
    },
    // NOTICE: KAPTION-INTEGRATION - Get iframe src with token (cached to prevent reloads)
    getIframeSrc(configItem, index) {
      // Return cached src if available — prevents iframe reload on token refresh
      if (this.initialSrcs[index]) return this.initialSrcs[index];

      if (this.isJwtModeEnabled && this.tokens[index]) {
        const url = new URL(configItem.url);
        url.searchParams.set('token', this.tokens[index]);
        const src = url.toString();
        this.initialSrcs = { ...this.initialSrcs, [index]: src };
        return src;
      }
      return configItem.url;
    },
    // NOTICE: KAPTION-INTEGRATION - Handle send message event from dashboard app SDK
    async handleSendMessage(payload, sourceWindow) {
      const { requestId, content, messageType, conversationId, attachments } =
        payload || {};

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] handleSendMessage:', {
        requestId,
        content,
        messageType,
        conversationId,
        hasAttachments: !!attachments?.length,
      });

      // Validate payload - content is required unless there are attachments
      const hasAttachments = attachments && attachments.length > 0;
      if (!hasAttachments && (!content || typeof content !== 'string')) {
        this.sendEventResult(
          sourceWindow,
          requestId,
          false,
          SDK_EVENT_TYPES.SEND_MESSAGE,
          {
            error: {
              code: 'INVALID_CONTENT',
              message: 'Message content is required',
            },
          }
        );
        return;
      }

      // Use provided conversationId or fall back to current chat
      const targetConversationId = conversationId || this.currentChat?.id;
      if (!targetConversationId) {
        this.sendEventResult(
          sourceWindow,
          requestId,
          false,
          SDK_EVENT_TYPES.SEND_MESSAGE,
          {
            error: {
              code: 'NO_CONVERSATION',
              message: 'No conversation context available',
            },
          }
        );
        return;
      }

      try {
        // Use shared utility to decode attachments
        const files = DashboardAppSDKUtils.decodeAttachments(attachments);

        // Dispatch message to store using the correct action
        const isPrivate = messageType === 'private';
        const messagePayload = {
          conversationId: targetConversationId,
          message: content || '',
          private: isPrivate,
        };

        // Add files if we have attachments
        if (files.length > 0) {
          messagePayload.files = files;
        }

        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );

        // eslint-disable-next-line no-console
        console.log('[Frame.vue] Message sent successfully', {
          hasAttachments: files.length > 0,
        });

        this.sendEventResult(
          sourceWindow,
          requestId,
          true,
          SDK_EVENT_TYPES.SEND_MESSAGE,
          {
            result: {
              sent: true,
              conversationId: targetConversationId,
              hasAttachments: files.length > 0,
            },
          }
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to send message:', error);

        this.sendEventResult(
          sourceWindow,
          requestId,
          false,
          SDK_EVENT_TYPES.SEND_MESSAGE,
          {
            error: {
              code: 'SEND_FAILED',
              message: error.message || 'Failed to send message',
            },
          }
        );
      }
    },
    // NOTICE: KAPTION-INTEGRATION - openConversation handler.
    // Thin adapter — the actual logic lives in DashboardAppSDKUtils so
    // both Frame.vue (sidebar/main) and the full-screen Index.vue
    // (routes/dashboard/fullscreen-app) share one implementation.
    async handleOpenConversation(payload, sourceWindow) {
      await DashboardAppSDKUtils.handleOpenConversation(payload, {
        accountId: this.accountId,
        store: this.$store,
        router: this.$router,
        currentRouteConversationId: this.$route?.params?.conversation_id,
        surface: this.surface,
        conversationApiShow: id => ConversationApi.show(id),
        buildConversationPath: (accountId, activeInbox, id) =>
          frontendURL(conversationUrl({ accountId, activeInbox, id })),
        ackResult: (success, sdkType, requestId, data) =>
          this.sendEventResult(sourceWindow, requestId, success, sdkType, data),
        onFrameClosed: detail =>
          this.$emit('frameClosedAfterNavigation', detail),
      });
    },
    // NOTICE: KAPTION-INTEGRATION - Send event result back to dashboard app SDK
    sendEventResult(sourceWindow, requestId, success, sdkType, data = {}) {
      if (!sourceWindow) {
        // eslint-disable-next-line no-console
        console.warn('[Frame.vue] No source window to send result to');
        return;
      }

      // Use shared utility to build message
      const message = DashboardAppSDKUtils.buildEventResultMessage(
        requestId,
        success,
        sdkType,
        data
      );

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] Sending event result:', message);

      try {
        sourceWindow.postMessage(JSON.stringify(message), '*');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to send event result:', error);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle log messages from dashboard apps
    handleLog(payload) {
      const { level, message, data, appId } = payload || {};
      // Use shared utility
      DashboardAppSDKUtils.logFromApp(level, message, data, appId);
    },
    // NOTICE: KAPTION-INTEGRATION - Handle set-badge event from dashboard apps
    handleSetBadge(payload, sourceWindow) {
      const { requestId, count, message, actions } = payload || {};

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] handleSetBadge:', {
        requestId,
        count,
        message,
        actions,
        appId: this.dashboardAppId,
      });

      if (!this.dashboardAppId) {
        this.sendBadgeResult(sourceWindow, requestId, false, {
          error: {
            code: 'NO_APP_ID',
            message: 'Dashboard app ID not available',
          },
        });
        return;
      }

      try {
        this.$store.dispatch('dashboardAppBadges/setBadge', {
          appId: this.dashboardAppId,
          count,
          message,
          badgeActions: actions,
        });

        this.sendBadgeResult(sourceWindow, requestId, true, {
          result: { badgeSet: true },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to set badge:', error);
        this.sendBadgeResult(sourceWindow, requestId, false, {
          error: {
            code: 'SET_BADGE_FAILED',
            message: error.message || 'Failed to set badge',
          },
        });
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle clear-badge event from dashboard apps
    handleClearBadge(payload, sourceWindow) {
      const { requestId } = payload || {};

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] handleClearBadge:', {
        requestId,
        appId: this.dashboardAppId,
      });

      if (!this.dashboardAppId) {
        this.sendBadgeResult(sourceWindow, requestId, false, {
          error: {
            code: 'NO_APP_ID',
            message: 'Dashboard app ID not available',
          },
        });
        return;
      }

      try {
        this.$store.dispatch('dashboardAppBadges/clearBadge', {
          appId: this.dashboardAppId,
        });

        this.sendBadgeResult(sourceWindow, requestId, true, {
          result: { badgeCleared: true },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to clear badge:', error);
        this.sendBadgeResult(sourceWindow, requestId, false, {
          error: {
            code: 'CLEAR_BADGE_FAILED',
            message: error.message || 'Failed to clear badge',
          },
        });
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Send badge result back to dashboard app SDK
    sendBadgeResult(sourceWindow, requestId, success, data = {}) {
      if (!sourceWindow) {
        // eslint-disable-next-line no-console
        console.warn('[Frame.vue] No source window to send badge result to');
        return;
      }

      const message = {
        type: 'chatwoot-dashboard-event-result',
        data: {
          requestId,
          success,
          sdkType: 'badge',
          ...data,
        },
      };

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] Sending badge result:', message);

      try {
        sourceWindow.postMessage(JSON.stringify(message), '*');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to send badge result:', error);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle send-notification event from dashboard apps
    handleSendNotification(payload, sourceWindow) {
      const { requestId, message, content, actions, notificationId } =
        payload || {};

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] handleSendNotification:', {
        requestId,
        message,
        content,
        actions,
        notificationId,
        appId: this.dashboardAppId,
      });

      if (!this.dashboardAppId) {
        this.sendNotificationResult(sourceWindow, requestId, false, {
          error: {
            code: 'NO_APP_ID',
            message: 'Dashboard app ID not available',
          },
        });
        return;
      }

      if (!message) {
        this.sendNotificationResult(sourceWindow, requestId, false, {
          error: {
            code: 'INVALID_MESSAGE',
            message: 'Notification message is required',
          },
        });
        return;
      }

      try {
        const result = this.$store.dispatch(
          'dashboardAppBadges/sendNotification',
          {
            appId: this.dashboardAppId,
            message,
            content: content || null,
            actions: actions || [],
            notificationId,
          }
        );

        // The dispatch returns a promise that resolves to { success, notificationId }
        result.then(response => {
          this.sendNotificationResult(sourceWindow, requestId, true, {
            result: {
              notificationSent: true,
              notificationId: response.notificationId,
            },
          });
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to send notification:', error);
        this.sendNotificationResult(sourceWindow, requestId, false, {
          error: {
            code: 'SEND_NOTIFICATION_FAILED',
            message: error.message || 'Failed to send notification',
          },
        });
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle dismiss-notification event from dashboard apps
    handleDismissNotification(payload, sourceWindow) {
      const { requestId, notificationId } = payload || {};

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] handleDismissNotification:', {
        requestId,
        notificationId,
        appId: this.dashboardAppId,
      });

      if (!notificationId) {
        this.sendNotificationResult(sourceWindow, requestId, false, {
          error: {
            code: 'INVALID_NOTIFICATION_ID',
            message: 'Notification ID is required',
          },
        });
        return;
      }

      try {
        this.$store.dispatch('dashboardAppBadges/dismissNotification', {
          notificationId,
        });

        this.sendNotificationResult(sourceWindow, requestId, true, {
          result: { notificationDismissed: true, notificationId },
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to dismiss notification:', error);
        this.sendNotificationResult(sourceWindow, requestId, false, {
          error: {
            code: 'DISMISS_NOTIFICATION_FAILED',
            message: error.message || 'Failed to dismiss notification',
          },
        });
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Send notification result back to dashboard app SDK
    sendNotificationResult(sourceWindow, requestId, success, data = {}) {
      if (!sourceWindow) {
        // eslint-disable-next-line no-console
        console.warn(
          '[Frame.vue] No source window to send notification result to'
        );
        return;
      }

      const message = {
        type: 'chatwoot-dashboard-event-result',
        data: {
          requestId,
          success,
          sdkType: 'notification',
          ...data,
        },
      };

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] Sending notification result:', message);

      try {
        sourceWindow.postMessage(JSON.stringify(message), '*');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to send notification result:', error);
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Handle copy-to-composer event from dashboard apps
    handleCopyToComposer(payload, sourceWindow) {
      const { requestId, content, attachments } = payload || {};

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] handleCopyToComposer:', {
        requestId,
        content,
        hasAttachments: !!attachments?.length,
      });

      try {
        // Use shared utility to emit copy-to-composer events
        const { hasContent, attachmentCount } =
          DashboardAppSDKUtils.emitCopyToComposer(content, attachments);

        // Send success result using unified sendEventResult
        this.sendEventResult(
          sourceWindow,
          requestId,
          true,
          SDK_EVENT_TYPES.COPY_TO_COMPOSER,
          {
            result: {
              populated: true,
              hasContent,
              attachmentCount,
            },
          }
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue] Failed to copy to composer:', error);

        this.sendEventResult(
          sourceWindow,
          requestId,
          false,
          SDK_EVENT_TYPES.COPY_TO_COMPOSER,
          {
            error: {
              code: 'COPY_FAILED',
              message: error.message || 'Failed to copy to composer',
            },
          }
        );
      }
    },
    // NOTICE: KAPTION-INTEGRATION - Send UI configuration to dashboard app
    /**
     * Send UI configuration to a specific iframe
     * @param {HTMLIFrameElement} frameElement - The iframe element
     * @param {Object} config - UI configuration object
     */
    sendUIConfig(frameElement, config = {}) {
      if (!frameElement || !frameElement.contentWindow) return;

      // Use shared utility to build message
      const message = DashboardAppSDKUtils.buildUIConfigMessage(config);

      // eslint-disable-next-line no-console
      console.log('[Frame.vue] Sending UI config:', message);
      DashboardAppSDKUtils.postMessageToIframe(
        frameElement,
        message,
        '[Frame.vue]'
      );
    },
    /**
     * Send UI config to all iframes (called on SDK ready)
     */
    sendUIConfigToAllIframes(config = {}) {
      this.config.forEach((_, index) => {
        const frameElement = document.getElementById(this.getFrameId(index));
        if (frameElement) {
          this.sendUIConfig(frameElement, config);
        }
      });
    },
    /**
     * Update UI config for all iframes (alias for backwards compatibility)
     * @param {Object} newConfig - New UI configuration
     */
    updateUIConfig(newConfig) {
      this.sendUIConfigToAllIframes(newConfig);
    },
    // NOTICE: KAPTION-INTEGRATION - Request context from dashboard app
    /**
     * Generate a unique request ID for tracking context responses
     * @returns {string} Unique request ID
     */
    generateRequestId() {
      return DashboardAppSDKUtils.generateRequestId();
    },
    /**
     * Request context data from dashboard app
     * @param {HTMLIFrameElement} frameElement - The iframe element
     * @param {string} contextType - Type of context requested
     * @param {Object} options - Request options
     * @returns {Promise<Object>} Promise resolving to context response
     */
    requestAppContext(frameElement, contextType, options = {}) {
      return new Promise((resolve, reject) => {
        if (!frameElement || !frameElement.contentWindow) {
          reject(new Error('Invalid frame element'));
          return;
        }

        const requestId = this.generateRequestId();
        const timeoutMs = options.timeout || 30000;

        // Use shared utility to build message
        const message = DashboardAppSDKUtils.buildContextRequestMessage(
          requestId,
          contextType,
          options
        );

        // Declare timeoutId before handler so it can be cleared
        let timeoutId;

        // Set up response handler
        const handleResponse = event => {
          if (!event.data) return;

          let messageData = event.data;
          if (typeof event.data === 'string') {
            try {
              messageData = JSON.parse(event.data);
            } catch (e) {
              return;
            }
          }

          if (
            messageData.type === 'chatwoot-dashboard-app-sdk' &&
            messageData.data?.sdkType === 'context-response' &&
            messageData.data?.payload?.requestId === requestId
          ) {
            clearTimeout(timeoutId);
            window.removeEventListener('message', handleResponse);

            const { success, data, error } = messageData.data.payload;
            if (success) {
              resolve(data);
            } else {
              reject(new Error(error?.message || 'Context request failed'));
            }
          }
        };

        // Set up timeout
        timeoutId = setTimeout(() => {
          window.removeEventListener('message', handleResponse);
          reject(new Error('Context request timeout'));
        }, timeoutMs);

        window.addEventListener('message', handleResponse);

        // eslint-disable-next-line no-console
        console.log('[Frame.vue] Requesting app context:', message);
        frameElement.contentWindow.postMessage(JSON.stringify(message), '*');
      });
    },
    /**
     * Request context from all iframes and return combined results
     * @param {string} contextType - Type of context requested
     * @param {Object} options - Request options
     * @returns {Promise<Array>} Promise resolving to array of context responses
     */
    // ============================================
    // Inter-App Tool & Context Protocol — Broker Methods
    // ============================================

    /**
     * Resolve app ID from source window
     */
    resolveAppIdFromWindow(sourceWindow) {
      for (let i = 0; i < this.config.length; i += 1) {
        const frameElement = document.getElementById(this.getFrameId(i));
        if (frameElement && frameElement.contentWindow === sourceWindow) {
          return `app-${this.surface}-${this.dashboardAppId || this.position}-${i}`;
        }
      }
      return null;
    },

    /**
     * Get the iframe window for a given app ID
     */
    getWindowForAppId(appId) {
      const entry = this.appRegistry[appId];
      if (!entry) return null;
      const frameElement = document.getElementById(entry.iframeId);
      return frameElement?.contentWindow || null;
    },

    /**
     * Build the full tool catalog from all registered apps + container
     */
    buildToolCatalog() {
      const catalog = [];
      // Container tools
      this.getContainerTools().forEach(tool => {
        catalog.push({ app: 'container', ...tool });
      });
      // App tools
      Object.entries(this.appRegistry).forEach(([appId, entry]) => {
        (entry.tools || []).forEach(tool => {
          catalog.push({ app: appId, ...tool });
        });
      });
      return catalog;
    },

    /**
     * Build the full context catalog from all registered apps + container
     */
    buildContextCatalog() {
      const catalog = [];
      // Container contexts
      this.getContainerContexts().forEach(ctx => {
        catalog.push({ app: 'container', ...ctx });
      });
      // App contexts
      Object.entries(this.appRegistry).forEach(([appId, entry]) => {
        (entry.contexts || []).forEach(ctx => {
          catalog.push({ app: appId, ...ctx });
        });
      });
      return catalog;
    },

    /**
     * Broadcast catalog-updated to all registered app iframes
     */
    broadcastCatalogUpdate() {
      const tools = this.buildToolCatalog();
      const contexts = this.buildContextCatalog();
      const message = DashboardAppSDKUtils.buildCatalogUpdatedMessage(
        tools,
        contexts
      );
      this.config.forEach((_, index) => {
        const frameElement = document.getElementById(this.getFrameId(index));
        if (frameElement) {
          DashboardAppSDKUtils.postMessageToIframe(
            frameElement,
            message,
            '[Frame.vue:Broker]'
          );
        }
      });
    },

    /**
     * Push catalog to a specific window (on SDK ready)
     */
    pushCatalogToWindow(targetWindow) {
      if (!targetWindow) return;
      const tools = this.buildToolCatalog();
      const contexts = this.buildContextCatalog();
      const message = DashboardAppSDKUtils.buildCatalogUpdatedMessage(
        tools,
        contexts
      );
      try {
        targetWindow.postMessage(JSON.stringify(message), '*');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue:Broker] Failed to push catalog:', error);
      }
    },

    /**
     * Post a message directly to a window (not via iframe element)
     */
    postMessageToWindow(targetWindow, message) {
      if (!targetWindow) return false;
      try {
        targetWindow.postMessage(JSON.stringify(message), '*');
        return true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Frame.vue:Broker] Failed to post message:', error);
        return false;
      }
    },

    // --- Container as Provider: Tool & Context Definitions ---

    getContainerTools() {
      return [
        {
          name: 'send_message',
          description: 'Send a message to the current conversation',
          parameters: {
            content: { type: 'string', required: true },
            private: { type: 'boolean', required: false },
          },
          timeout: 30000,
        },
        {
          name: 'send_private_note',
          description: 'Send a private note to the current conversation',
          parameters: {
            content: { type: 'string', required: true },
          },
          timeout: 30000,
        },
        {
          name: 'copy_to_composer',
          description: 'Copy content to the chat composer',
          parameters: {
            content: { type: 'string', required: false },
            attachments: { type: 'array', required: false },
          },
          timeout: 10000,
        },
        {
          name: 'set_badge',
          description: 'Set notification badge on the app tab',
          parameters: {
            count: { type: 'number', required: false },
            message: { type: 'string', required: false },
            actions: { type: 'array', required: false },
          },
          timeout: 5000,
        },
        {
          name: 'clear_badge',
          description: 'Clear notification badge from the app tab',
          parameters: {},
          timeout: 5000,
        },
        {
          name: 'send_notification',
          description: 'Send a notification to the agent',
          parameters: {
            message: { type: 'string', required: true },
            content: { type: 'string', required: false },
            actions: { type: 'array', required: false },
          },
          timeout: 10000,
        },
        {
          // NOTICE: KAPTION-INTEGRATION
          name: 'open_conversation',
          description:
            'Deep-link the agent to a specific conversation by id. Surface-aware: ' +
            'full-screen apps collapse by default after navigation.',
          parameters: {
            conversationId: { type: 'number', required: true },
            inboxId: { type: 'number', required: false },
            target: { type: 'string', required: false }, // 'self' | 'blank'
            closeFullScreen: { type: 'boolean', required: false },
          },
          timeout: 5000,
        },
      ];
    },

    getContainerContexts() {
      return [
        {
          name: 'conversation',
          description: 'Current conversation data',
        },
        {
          name: 'contact',
          description: 'Current contact data',
        },
        {
          name: 'current_agent',
          description: 'Currently logged-in agent',
        },
        {
          name: 'user_preferences',
          description: 'User preferences (theme, language, timezone)',
        },
      ];
    },

    /**
     * Execute a container tool directly
     */
    async executeContainerTool(tool, params, callerWindow, callerRequestId) {
      try {
        let result;
        switch (tool) {
          case 'send_message':
            await this.handleSendMessage(
              {
                requestId: callerRequestId,
                content: params.content,
                messageType: params.private ? 'private' : 'outgoing',
                conversationId: this.currentChat?.id,
                attachments: params.attachments,
              },
              callerWindow
            );
            return; // handleSendMessage sends its own result

          case 'send_private_note':
            await this.handleSendMessage(
              {
                requestId: callerRequestId,
                content: params.content,
                messageType: 'private',
                conversationId: this.currentChat?.id,
              },
              callerWindow
            );
            return;

          case 'copy_to_composer':
            this.handleCopyToComposer(
              {
                requestId: callerRequestId,
                content: params.content,
                attachments: params.attachments,
              },
              callerWindow
            );
            return;

          case 'set_badge':
            this.handleSetBadge(
              {
                requestId: callerRequestId,
                count: params.count,
                message: params.message,
                actions: params.actions,
              },
              callerWindow
            );
            return;

          case 'clear_badge':
            this.handleClearBadge({ requestId: callerRequestId }, callerWindow);
            return;

          case 'send_notification':
            this.handleSendNotification(
              {
                requestId: callerRequestId,
                message: params.message,
                content: params.content,
                actions: params.actions,
              },
              callerWindow
            );
            return;

          case 'open_conversation':
            // NOTICE: KAPTION-INTEGRATION
            await this.handleOpenConversation(
              {
                requestId: callerRequestId,
                conversationId: params.conversationId,
                inboxId: params.inboxId,
                target: params.target,
                closeFullScreen: params.closeFullScreen,
              },
              callerWindow
            );
            return;

          default:
            result = {
              error: {
                code: 'UNKNOWN_TOOL',
                message: `Unknown container tool: ${tool}`,
              },
            };
            this.sendEventResult(
              callerWindow,
              callerRequestId,
              false,
              'tool-result',
              result
            );
        }
      } catch (error) {
        this.sendEventResult(
          callerWindow,
          callerRequestId,
          false,
          'tool-result',
          { error: { code: 'CONTAINER_ERROR', message: error.message } }
        );
      }
    },

    /**
     * Execute a container context fetch directly
     */
    executeContainerContextFetch(contextName, callerWindow, callerRequestId) {
      let data;
      switch (contextName) {
        case 'conversation':
          data = this.dashboardAppContext.conversation;
          break;
        case 'contact':
          data = this.dashboardAppContext.contact;
          break;
        case 'current_agent':
          data = this.dashboardAppContext.currentAgent;
          break;
        case 'user_preferences': {
          const uiSettings = this.$store.getters.getUISettings || {};
          data = {
            theme: uiSettings.theme || 'light',
            language: this.$i18n.locale,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          };
          break;
        }
        default:
          this.sendEventResult(
            callerWindow,
            callerRequestId,
            false,
            'ctx-result',
            {
              error: {
                code: 'UNKNOWN_CONTEXT',
                message: `Unknown container context: ${contextName}`,
              },
            }
          );
          return;
      }

      this.sendEventResult(callerWindow, callerRequestId, true, 'ctx-result', {
        data,
      });
    },

    // --- Broker Event Handlers ---

    handleBrokerToolRegister(payload, sourceWindow) {
      const appId = this.resolveAppIdFromWindow(sourceWindow);
      if (!appId) return;

      if (!this.appRegistry[appId]) {
        this.appRegistry[appId] = { tools: [], contexts: [], iframeId: '' };
      }
      // Find iframe ID for this window
      for (let i = 0; i < this.config.length; i += 1) {
        const fId = this.getFrameId(i);
        const el = document.getElementById(fId);
        if (el && el.contentWindow === sourceWindow) {
          this.appRegistry[appId].iframeId = fId;
          break;
        }
      }

      const newTools = payload.tools || [];
      // Merge: replace existing by name, add new
      const existing = this.appRegistry[appId].tools.filter(
        t => !newTools.some(nt => nt.name === t.name)
      );
      this.appRegistry[appId].tools = [...existing, ...newTools];
      this.broadcastCatalogUpdate();
    },

    handleBrokerToolUnregister(payload, sourceWindow) {
      const appId = this.resolveAppIdFromWindow(sourceWindow);
      if (!appId || !this.appRegistry[appId]) return;

      const names = payload.toolNames || [];
      this.appRegistry[appId].tools = this.appRegistry[appId].tools.filter(
        t => !names.includes(t.name)
      );
      this.broadcastCatalogUpdate();
    },

    handleBrokerCtxRegister(payload, sourceWindow) {
      const appId = this.resolveAppIdFromWindow(sourceWindow);
      if (!appId) return;

      if (!this.appRegistry[appId]) {
        this.appRegistry[appId] = { tools: [], contexts: [], iframeId: '' };
      }
      for (let i = 0; i < this.config.length; i += 1) {
        const fId = this.getFrameId(i);
        const el = document.getElementById(fId);
        if (el && el.contentWindow === sourceWindow) {
          this.appRegistry[appId].iframeId = fId;
          break;
        }
      }

      const newCtxs = payload.contexts || [];
      const existing = this.appRegistry[appId].contexts.filter(
        c => !newCtxs.some(nc => nc.name === c.name)
      );
      this.appRegistry[appId].contexts = [...existing, ...newCtxs];
      this.broadcastCatalogUpdate();
    },

    handleBrokerCtxUnregister(payload, sourceWindow) {
      const appId = this.resolveAppIdFromWindow(sourceWindow);
      if (!appId || !this.appRegistry[appId]) return;

      const names = payload.contextNames || [];
      this.appRegistry[appId].contexts = this.appRegistry[
        appId
      ].contexts.filter(c => !names.includes(c.name));
      this.broadcastCatalogUpdate();
    },

    handleBrokerToolDiscover(payload, sourceWindow) {
      const tools = this.buildToolCatalog();
      const message = DashboardAppSDKUtils.buildToolCatalogMessage(
        payload.requestId,
        tools
      );
      this.postMessageToWindow(sourceWindow, message);
    },

    handleBrokerCtxDiscover(payload, sourceWindow) {
      const contexts = this.buildContextCatalog();
      const message = DashboardAppSDKUtils.buildCtxCatalogMessage(
        payload.requestId,
        contexts
      );
      this.postMessageToWindow(sourceWindow, message);
    },

    handleBrokerToolInvoke(payload, sourceWindow) {
      const { targetApp, tool, params, requestId: callerRequestId } = payload;

      // Container as target
      if (targetApp === 'container') {
        this.executeContainerTool(tool, params, sourceWindow, callerRequestId);
        return;
      }

      // Look up target app in registry
      const targetEntry = this.appRegistry[targetApp];
      if (!targetEntry) {
        this.sendEventResult(
          sourceWindow,
          callerRequestId,
          false,
          'tool-result',
          {
            error: {
              code: 'APP_NOT_FOUND',
              message: `App not found: ${targetApp}`,
            },
          }
        );
        return;
      }

      // Verify tool exists in target app's registry
      const toolDef = targetEntry.tools.find(t => t.name === tool);
      if (!toolDef) {
        this.sendEventResult(
          sourceWindow,
          callerRequestId,
          false,
          'tool-result',
          {
            error: {
              code: 'TOOL_NOT_FOUND',
              message: `Tool '${tool}' not found on app '${targetApp}'`,
            },
          }
        );
        return;
      }

      // Generate container-scoped request ID
      const containerRequestId = DashboardAppSDKUtils.generateRequestId();

      // Determine timeout
      const timeout = toolDef.timeout || 30000;
      const timeoutId = setTimeout(() => {
        // Timeout: cancel on provider, error to caller
        const targetWindow = this.getWindowForAppId(targetApp);
        if (targetWindow) {
          this.postMessageToWindow(
            targetWindow,
            DashboardAppSDKUtils.buildToolCancelMessage(containerRequestId)
          );
        }
        this.sendEventResult(
          sourceWindow,
          callerRequestId,
          false,
          'tool-result',
          { error: { code: 'TIMEOUT', message: 'Tool invocation timed out' } }
        );
        delete this.brokerPendingInvocations[containerRequestId];
      }, timeout);

      // Store pending invocation
      this.brokerPendingInvocations[containerRequestId] = {
        callerRequestId,
        callerWindow: sourceWindow,
        targetApp,
        tool,
        timeoutId,
      };

      // Route to target app
      const targetWindow = this.getWindowForAppId(targetApp);
      if (!targetWindow) {
        clearTimeout(timeoutId);
        delete this.brokerPendingInvocations[containerRequestId];
        this.sendEventResult(
          sourceWindow,
          callerRequestId,
          false,
          'tool-result',
          {
            error: {
              code: 'APP_UNAVAILABLE',
              message: `App '${targetApp}' iframe not available`,
            },
          }
        );
        return;
      }

      const invokeMessage = DashboardAppSDKUtils.buildToolInvokeMessage(
        containerRequestId,
        callerRequestId,
        this.resolveAppIdFromWindow(sourceWindow) || 'unknown',
        tool,
        params
      );
      this.postMessageToWindow(targetWindow, invokeMessage);
    },

    handleBrokerToolResult(payload) {
      const { requestId, data } = payload;
      const pending = this.brokerPendingInvocations[requestId];
      if (!pending) return;

      clearTimeout(pending.timeoutId);
      this.sendEventResult(
        pending.callerWindow,
        pending.callerRequestId,
        true,
        'tool-result',
        { data }
      );
      delete this.brokerPendingInvocations[requestId];
    },

    handleBrokerToolPartial(payload) {
      const { requestId, data } = payload;
      const pending = this.brokerPendingInvocations[requestId];
      if (!pending) return;

      const message = DashboardAppSDKUtils.buildEventPartialMessage(
        pending.callerRequestId,
        { data }
      );
      this.postMessageToWindow(pending.callerWindow, message);
    },

    handleBrokerToolError(payload) {
      const { requestId, error } = payload;
      const pending = this.brokerPendingInvocations[requestId];
      if (!pending) return;

      clearTimeout(pending.timeoutId);
      this.sendEventResult(
        pending.callerWindow,
        pending.callerRequestId,
        false,
        'tool-result',
        { error }
      );
      delete this.brokerPendingInvocations[requestId];
    },

    handleBrokerToolCancel(payload) {
      const callerRequestId = payload.requestId;
      // Find the pending invocation by callerRequestId
      const entry = Object.entries(this.brokerPendingInvocations).find(
        ([, v]) => v.callerRequestId === callerRequestId
      );
      if (!entry) return;

      const [containerRequestId, pending] = entry;
      clearTimeout(pending.timeoutId);

      // Forward cancel to provider
      const targetWindow = this.getWindowForAppId(pending.targetApp);
      if (targetWindow) {
        this.postMessageToWindow(
          targetWindow,
          DashboardAppSDKUtils.buildToolCancelMessage(containerRequestId)
        );
      }

      // Don't remove from pending — let the provider respond with CANCELLED error
      // Or clean up if provider doesn't respond within a short grace period
      setTimeout(() => {
        if (this.brokerPendingInvocations[containerRequestId]) {
          this.sendEventResult(
            pending.callerWindow,
            callerRequestId,
            false,
            'tool-result',
            { error: { code: 'CANCELLED', message: 'Invocation cancelled' } }
          );
          delete this.brokerPendingInvocations[containerRequestId];
        }
      }, 2000);
    },

    handleBrokerCtxFetch(payload, sourceWindow) {
      const { targetApp, contextName, requestId: callerRequestId } = payload;

      if (targetApp === 'container') {
        this.executeContainerContextFetch(
          contextName,
          sourceWindow,
          callerRequestId
        );
        return;
      }

      const targetEntry = this.appRegistry[targetApp];
      if (!targetEntry) {
        this.sendEventResult(
          sourceWindow,
          callerRequestId,
          false,
          'ctx-result',
          {
            error: {
              code: 'APP_NOT_FOUND',
              message: `App not found: ${targetApp}`,
            },
          }
        );
        return;
      }

      const ctxDef = targetEntry.contexts.find(c => c.name === contextName);
      if (!ctxDef) {
        this.sendEventResult(
          sourceWindow,
          callerRequestId,
          false,
          'ctx-result',
          {
            error: {
              code: 'CONTEXT_NOT_FOUND',
              message: `Context '${contextName}' not found on app '${targetApp}'`,
            },
          }
        );
        return;
      }

      const containerRequestId = DashboardAppSDKUtils.generateRequestId();
      const timeout = 30000;
      const timeoutId = setTimeout(() => {
        this.sendEventResult(
          sourceWindow,
          callerRequestId,
          false,
          'ctx-result',
          { error: { code: 'TIMEOUT', message: 'Context fetch timed out' } }
        );
        delete this.brokerPendingInvocations[containerRequestId];
      }, timeout);

      this.brokerPendingInvocations[containerRequestId] = {
        callerRequestId,
        callerWindow: sourceWindow,
        targetApp,
        contextName,
        timeoutId,
        isCtx: true,
      };

      const targetWindow = this.getWindowForAppId(targetApp);
      if (!targetWindow) {
        clearTimeout(timeoutId);
        delete this.brokerPendingInvocations[containerRequestId];
        this.sendEventResult(
          sourceWindow,
          callerRequestId,
          false,
          'ctx-result',
          {
            error: {
              code: 'APP_UNAVAILABLE',
              message: `App '${targetApp}' iframe not available`,
            },
          }
        );
        return;
      }

      const fetchMessage = DashboardAppSDKUtils.buildCtxFetchMessage(
        containerRequestId,
        callerRequestId,
        this.resolveAppIdFromWindow(sourceWindow) || 'unknown',
        contextName,
        {}
      );
      this.postMessageToWindow(targetWindow, fetchMessage);
    },

    handleBrokerCtxResult(payload) {
      const { requestId, data } = payload;
      const pending = this.brokerPendingInvocations[requestId];
      if (!pending || !pending.isCtx) return;

      clearTimeout(pending.timeoutId);
      this.sendEventResult(
        pending.callerWindow,
        pending.callerRequestId,
        true,
        'ctx-result',
        { data }
      );
      delete this.brokerPendingInvocations[requestId];
    },

    handleBrokerCtxError(payload) {
      const { requestId, error } = payload;
      const pending = this.brokerPendingInvocations[requestId];
      if (!pending || !pending.isCtx) return;

      clearTimeout(pending.timeoutId);
      this.sendEventResult(
        pending.callerWindow,
        pending.callerRequestId,
        false,
        'ctx-result',
        { error }
      );
      delete this.brokerPendingInvocations[requestId];
    },

    // --- Cleanup on unmount ---

    cleanupBrokerState() {
      // Clear all pending invocations and their timeouts
      Object.values(this.brokerPendingInvocations).forEach(pending => {
        if (pending.timeoutId) clearTimeout(pending.timeoutId);
      });
      this.brokerPendingInvocations = {};
      this.appRegistry = {};
    },

    async requestAppContextFromAll(contextType, options = {}) {
      const promises = this.config.map((_, index) => {
        const frameElement = document.getElementById(this.getFrameId(index));
        if (frameElement) {
          return this.requestAppContext(
            frameElement,
            contextType,
            options
          ).catch(error => {
            // eslint-disable-next-line no-console
            console.warn(
              `[Frame.vue] Context request failed for frame ${index}:`,
              error
            );
            return null;
          });
        }
        return Promise.resolve(null);
      });

      const results = await Promise.all(promises);
      return results.filter(result => result !== null);
    },
  },
};
</script>

<!-- NOTICE: KAPTION-INTEGRATION - Iframe renders immediately, no loading state -->
<!-- Parent component uses v-show to hide/show this frame without destroying it -->
<template>
  <div class="dashboard-app--container">
    <div
      v-for="(configItem, index) in config"
      :key="index"
      class="dashboard-app--list"
    >
      <iframe
        v-if="
          configItem.type === 'frame' &&
          configItem.url &&
          (!isJwtModeEnabled || tokens[index])
        "
        :id="getFrameId(index)"
        :src="getIframeSrc(configItem, index)"
        @load="() => onIframeLoad(index)"
      />
    </div>
  </div>
</template>

<style scoped>
.dashboard-app--container,
.dashboard-app--list,
.dashboard-app--list iframe {
  height: 100%;
  width: 100%;
}

.dashboard-app--list iframe {
  border: 0;
}
</style>
